import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useIntl } from 'react-intl';

import {
  DELETE_FINDS_MUTATION,
  removeFindsFromCache,
  removeFindTagAssignmentsFromCache,
} from '../../gql';
import { Button } from '../design-system';

interface FindBatchDeleteDialogProps {
  findIds: readonly string[];
  onClose: () => void;
  onDeleted?: () => void;
}

const contentStyles = { minWidth: { sm: 420 } };

export function FindBatchDeleteDialog({
  findIds,
  onClose,
  onDeleted,
}: FindBatchDeleteDialogProps) {
  const intl = useIntl();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [deleteFinds, { loading, error }] = useMutation(DELETE_FINDS_MUTATION, {
    update: (cache, { data }) => {
      const deletedIds = data?.deleteFinds?.deletedIds;

      if (deletedIds?.length) {
        removeFindTagAssignmentsFromCache(cache, deletedIds);
        removeFindsFromCache(cache, deletedIds);
      }
    },
  });
  const handleDialogClose = useCallback(() => {
    if (!loading) {
      onClose();
    }
  }, [loading, onClose]);
  const handleDelete = useCallback(async () => {
    setValidationError(null);

    try {
      const result = await deleteFinds({ variables: { ids: findIds } });
      const mutationResult = result.data?.deleteFinds;

      if (mutationResult?.errors?.length) {
        setValidationError(mutationResult.errors[0].error);
        return;
      }

      onDeleted?.();
      onClose();
    } catch {
      // Apollo exposes the network error below and keeps the dialog open.
    }
  }, [deleteFinds, findIds, onClose, onDeleted]);

  return (
    <Dialog open onClose={handleDialogClose}>
      <DialogTitle>
        {intl.formatMessage(
          {
            id: 'finds.batch.deleteTitle',
            defaultMessage:
              'Delete {count, plural, one {# find} other {# finds}}?',
          },
          { count: findIds.length }
        )}
      </DialogTitle>
      <DialogContent sx={contentStyles}>
        <Stack gap={2}>
          <DialogContentText>
            {intl.formatMessage({
              id: 'finds.batch.deletePermissionNote',
              defaultMessage:
                'Only finds you have access level for editing to will be deleted. Others will be skipped.',
            })}
          </DialogContentText>
          <DialogContentText>
            {intl.formatMessage({
              id: 'finds.batch.deleteWarning',
              defaultMessage: 'This action cannot be undone.',
            })}
          </DialogContentText>
          {(validationError || error) && (
            <Alert severity="error" variant="outlined">
              {validationError ||
                intl.formatMessage({
                  id: 'finds.batch.deleteError',
                  defaultMessage: 'The finds could not be deleted.',
                })}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button tone="neutral" disabled={loading} onClick={handleDialogClose}>
          {intl.formatMessage({
            id: 'general.cancel',
            defaultMessage: 'Cancel',
          })}
        </Button>
        <Button tone="danger" disabled={loading} onClick={handleDelete}>
          {intl.formatMessage({
            id: 'general.delete',
            defaultMessage: 'Delete',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
